import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Home, Plus, FileText, Package, BadgeCheck, Calendar as CalendarIcon,
  ArrowUpDown, Trash2, ShoppingCart, Mail, ReceiptPoundSterling, ShieldCheck, Truck,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import OrdersTabsHeader, { ORDERS_GRANULAR_TAB_STATUS, useOrdersTab } from '@/components/OrdersTabsHeader';
import OrderStageTimeline from '@/components/OrderStageTimeline';
import { formatApiError } from '@/utils/apiError';
import { InstallerPayStep, OrderTotal } from '@/components/orders/InstallerPayStep';

const PRIMARY = '#2F5E96';

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const STATUS_COLOURS = {
  draft:             'bg-sky-100 text-sky-800',
  submitted:         'bg-amber-100 text-amber-800',
  pending:           'bg-amber-100 text-amber-800',
  invoiced:          'bg-indigo-100 text-indigo-800',
  paid:              'bg-teal-100 text-teal-800',
  payment_confirmed: 'bg-blue-100 text-blue-800',
  dispatched:        'bg-purple-100 text-purple-800',
  delivered:         'bg-emerald-100 text-emerald-800',
  cancelled:         'bg-rose-100 text-rose-800',
};

// Each granular tab gets its own empty-state copy so the page reads well
// when an installer drills into a stage they haven't reached yet.
const EMPTY_STATE = {
  drafts:            { icon: FileText,             title: 'No drafts',                       blurb: 'Start a new order from the button above. Drafts stay private until you submit them to Ox.' },
  awaiting_invoice:  { icon: Mail,                 title: 'No orders awaiting invoice',      blurb: 'When you submit an order, it lands here while Ox prepare the invoice.' },
  ready_for_payment: { icon: ReceiptPoundSterling, title: 'No invoices ready to pay',        blurb: 'Once Ox have raised the invoice, your order moves here and you\u2019ll be emailed the invoice number.' },
  payment_made:      { icon: BadgeCheck,           title: 'Nothing awaiting payment confirmation', blurb: 'Mark an invoiced order as paid and it will sit here until Ox confirm the funds have landed.' },
  payment_received:  { icon: ShieldCheck,          title: 'No orders awaiting dispatch',     blurb: 'Once Ox confirm your payment, the order moves here and into their dispatch queue.' },
  dispatched:        { icon: Package,              title: 'No dispatched orders yet',        blurb: 'Dispatched and delivered orders will be archived here for your records.' },
};

export default function ProBoundOrders() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortDir, setSortDir] = useState('desc'); // 'desc' = newest first
  const [pendingDelete, setPendingDelete] = useState(null); // order object pending confirmation
  const [deleting, setDeleting] = useState(false);
  const isStaff = user?.role && ['admin', 'manager', 'team'].includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes`, { credentials: 'include' });
      if (res.ok) setOrders(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    fetch(`${API_URL}/api/notifications/mark-read-by-category/order?service_type=probound`, {
      method: 'POST', credentials: 'include',
    }).catch(() => {});
  }, []);

  // Normalise legacy "pending" → "submitted" on the client too
  const normalised = useMemo(() => orders.map(o => ({ ...o, status: o.status === 'pending' ? 'submitted' : o.status })), [orders]);

  const [tab] = useOrdersTab('awaiting_invoice');
  const counts = useMemo(() => {
    const c = {};
    for (const key of Object.keys(ORDERS_GRANULAR_TAB_STATUS)) {
      const allowed = ORDERS_GRANULAR_TAB_STATUS[key];
      c[key] = normalised.filter(o => allowed.includes(o.status)).length;
    }
    return c;
  }, [normalised]);

  const visibleOrders = useMemo(() => {
    const allowed = ORDERS_GRANULAR_TAB_STATUS[tab] || ORDERS_GRANULAR_TAB_STATUS.awaiting_invoice;
    const filtered = normalised.filter(o => allowed.includes(o.status));
    const sorted = [...filtered].sort((a, b) => {
      const ta = new Date(a.created_at).getTime() || 0;
      const tb = new Date(b.created_at).getTime() || 0;
      return sortDir === 'desc' ? tb - ta : ta - tb;
    });
    return sorted;
  }, [normalised, tab, sortDir]);

  const updateStatus = async (orderId, nextStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/quotes/${orderId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(typeof nextStatus === 'object' ? nextStatus : { status: nextStatus }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Update failed');
      toast.success(typeof nextStatus === 'object' ? 'Thanks — Ox Resin will confirm your payment' : `Order moved to ${nextStatus}`);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/quotes/${pendingDelete.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Delete failed');
      toast.success(`Order ${pendingDelete.reference} deleted`);
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${PRIMARY}, #1f4068)` }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-4">
          <button onClick={() => navigate(-1)} data-testid="probound-orders-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl md:text-2xl font-bold truncate">Material Orders</h1>
            <p className="hidden sm:block text-white/70 text-sm">Submitted orders and completed orders — track invoice, payment, dispatch and delivery for your ProBound material orders.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => navigate('/order')}
              size="sm"
              className="bg-white text-[#2F5E96] hover:bg-white/90 font-bold px-2 sm:px-3"
              aria-label="New Order"
            >
              <Plus className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">New Order</span>
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-2 sm:px-3"
              aria-label="Home"
            >
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Hero CTA — bigger, friendlier than the icon button in the header. */}
        <Card className="p-4 sm:p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border-2" style={{ borderColor: PRIMARY + '33' }}>
          <div
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: PRIMARY + '22', color: PRIMARY }}
          >
            <ShoppingCart className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-900">Need materials for a job?</h2>
            <p className="text-xs sm:text-sm text-gray-500">Spin up a fresh ProBound order, save it as a draft, then submit when you&apos;re ready.</p>
          </div>
          <Button
            onClick={() => navigate('/order')}
            data-testid="probound-build-new-order"
            size="lg"
            className="font-bold w-full sm:w-auto"
            style={{ background: PRIMARY, color: '#fff' }}
          >
            <Plus className="w-4 h-4 mr-1.5" /> Build a new order
          </Button>
        </Card>

        <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0 overflow-x-auto">
            <OrdersTabsHeader counts={counts} showDrafts accent={PRIMARY} ink="#fff" granular />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="probound-orders-sort-toggle"
            onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            className="self-start sm:self-auto whitespace-nowrap"
            title="Toggle date order"
          >
            <ArrowUpDown className="w-4 h-4 mr-1.5" />
            {sortDir === 'desc' ? 'Newest first' : 'Oldest first'}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading orders…</div>
        ) : visibleOrders.length === 0 ? (
          (() => {
            const e = EMPTY_STATE[tab] || EMPTY_STATE.awaiting_invoice;
            const EIcon = e.icon;
            return (
              <Card className="p-12 text-center" data-testid={`probound-orders-empty-${tab}`}>
                <EIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-bold text-gray-900 mb-1">{e.title}</h3>
                <p className="text-gray-500 text-sm mb-4 max-w-md mx-auto">{e.blurb}</p>
                <Button onClick={() => navigate('/order')} className="bg-[#2F5E96] hover:bg-[#1f4068] text-white font-bold">
                  <Plus className="w-4 h-4 mr-1.5" /> Build a new order
                </Button>
              </Card>
            );
          })()
        ) : (
          <div className="space-y-3">
            {visibleOrders.map((order) => {
              const total = order.pricing_snapshot?.total_inc_vat ?? order.calculation?.totalCost ?? order.total ?? 0;
              const customerName = order.customer_details?.name || order.customer_reference || '—';
              const canDelete = order.status === 'submitted';
              return (
                <Card
                  key={order.id}
                  data-testid={`probound-order-${order.reference}`}
                  onClick={() => navigate(`/probound/orders/${order.id}`)}
                  className="p-4 md:p-5 cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <h3 className="font-bold text-gray-900">{order.reference}</h3>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLOURS[order.status] || ''}`}>
                          {order.status}
                        </span>
                        {order.invoice_number && (
                          <span className="text-[10px] uppercase tracking-wider text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
                            INV {order.invoice_number}
                          </span>
                        )}
                        {order.expected_delivery_date && (
                          <span className="text-[10px] uppercase tracking-wider text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" /> ETA {order.expected_delivery_date}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Customer: <strong>{customerName}</strong> · {new Date(order.created_at).toLocaleDateString('en-GB')}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(order.blend_areas || []).length} area{(order.blend_areas || []).length !== 1 ? 's' : ''}
                      </div>
                      <div className="mt-2">
                        <OrderStageTimeline order={order} compact hideUnreached />
                      </div>
                    </div>
                    <OrderTotal order={order} appTotal={total} color={'#2F5E96'} testId={`probound-total-${order.reference}`} />
                  </div>

                  <div
                    className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <InstallerPayStep order={order} testPrefix="probound" business="probound" onMarkPaid={(body) => updateStatus(order.id, body)} />
                    {order.status === 'paid' && (
                      <span className="text-xs text-gray-500 self-center italic">Ox Resin are confirming payment received…</span>
                    )}
                    {order.status === 'payment_confirmed' && (
                      <span className="text-xs text-blue-700 self-center italic">
                        <ShieldCheck className="w-3 h-3 inline mr-1" />
                        Payment received — in Ox dispatch queue.
                      </span>
                    )}
                    {order.status === 'dispatched' && (
                      <span className="text-xs text-purple-700 self-center italic">
                        <Truck className="w-3 h-3 inline mr-1" />
                        Dispatched{order.expected_delivery_date ? ` — ETA ${order.expected_delivery_date}` : ''}
                      </span>
                    )}

                    {isStaff && order.project_id && (
                      <Link
                        to={`/projects/${order.project_id}`}
                        className="text-xs text-[#2F5E96] underline self-center"
                      >
                        View project →
                      </Link>
                    )}

                    {canDelete && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid={`probound-delete-order-${order.reference}`}
                        onClick={() => setPendingDelete(order)}
                        className="ml-auto text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800 font-semibold"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="probound-delete-confirm-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submitted order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete this submitted quote from both your dashboard and the Ox Resin Processing Queue, are you sure you want to delete{pendingDelete?.reference ? ` ${pendingDelete.reference}` : ''}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="probound-delete-cancel" disabled={deleting}>No</AlertDialogCancel>
            <AlertDialogAction
              data-testid="probound-delete-confirm"
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
